// Again — one-tap add-on in the cart drawer. Uses Horizon's own cart event
// contract (CartLinesUpdateEvent + section HTML) so the drawer morphs in place.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Add-on with options (Insole: size; Recovery Slides: colour + size): the drawer renders native <select data-addon-opt>
// (keyboard accessible; size defaulted in Liquid only on an exact match to the shoe size in the cart) + the variants
// as JSON; Add needs every option, else the inline error names the missing one and focus goes back to it.
class AgainCartAddon extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.button = this.querySelector('button');
    // one select per option with more than one value (snippet again-cart-upsell): Insole = size, Slides = colour + size
    this.selects = [...this.querySelectorAll('[data-addon-opt]')];
    this.err = this.querySelector('[data-addon-err]');
    try {
      this.variants = JSON.parse(this.querySelector('[data-addon-variants]')?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.button?.addEventListener('click', () => this.add());
    this.selects.forEach((s) =>
      s.addEventListener('change', () => {
        if (this.selects.every((x) => x.value)) this.showError('');
      })
    );
  }

  showError(msg, bad = []) {
    this.selects.forEach((s) => s.toggleAttribute('aria-invalid', bad.includes(s)));
    if (!this.err) return;
    this.err.textContent = msg;
    this.err.hidden = !msg;
  }

  variantId() {
    if (!this.selects.length) return Number(this.dataset.variantId);
    const empty = this.selects.filter((s) => !s.value);
    if (empty.length) {
      this.showError(`Pick your ${empty[0].dataset.name || 'option'} first.`, empty);
      empty[0].focus();
      return 0;
    }
    const v = this.variants.find((x) => this.selects.every((s) => x.o[Number(s.dataset.i)] === s.value));
    if (!v || !v.a) {
      this.showError('That one is not available. Try another.', this.selects);
      this.selects[0].focus();
      return 0;
    }
    return v.id;
  }

  async add() {
    if (!this.button || this.button.getAttribute('aria-busy') === 'true') return;
    const id = this.variantId();
    if (!id) return;
    this.showError('');
    this.button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const added = await res.json();
      const failed = !res.ok || Boolean(added.status);
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: failed ? undefined : added.sections,
          source: 'again-cart-addon',
          didError: failed,
        },
      });
      if (failed) this.showError(added.description || added.message || 'Could not add it. Please try again.');
    } catch (error) {
      deferred.reject(error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      // Cleared on every outcome: on an HTTP error (e.g. sold out) the drawer may not morph,
      // and a button left busy would be dead.
      this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('again-cart-addon')) customElements.define('again-cart-addon', AgainCartAddon);

// Again — "Add a second pair free" in the cart drawer (one pair in the cart). The mini picker
// (again-cart-extras.liquid) holds that style's colour + size; this adds the matching 1-pair variant.
// The store's automatic discount "Again · Buy 1 get 1 free" makes it free; nothing is swapped.
class AgainFreePair extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.button = this.querySelector('[data-fp-add]');
    this.err = this.querySelector('[data-fp-err]');
    try {
      this.variants = JSON.parse(this.querySelector('[data-fp-variants]')?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.button?.addEventListener('click', () => this.add());
  }

  pick() {
    const c = Number(this.dataset.c);
    const s = Number(this.dataset.s);
    const col = this.querySelector('[data-fp-color]')?.value;
    const size = this.querySelector('[data-fp-size]')?.value;
    return this.variants.find((v) => (c < 0 || v.o[c] === col) && (s < 0 || v.o[s] === size));
  }

  async add() {
    if (!this.button || this.button.getAttribute('aria-busy') === 'true') return;
    const v = this.pick();
    if (this.err) this.err.hidden = true;
    if (!v || !v.a) {
      if (this.err) {
        this.err.textContent = 'That colour and size is not available. Try another.';
        this.err.hidden = false;
      }
      return;
    }
    this.button.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(v.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }], sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const added = await res.json();
      const failed = !res.ok || Boolean(added.status);
      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: failed ? undefined : added.sections, source: 'again-free-pair', didError: failed },
      });
      if (failed && this.err) {
        this.err.textContent = added.description || added.message || 'Could not add that pair.';
        this.err.hidden = false;
      }
    } catch (error) {
      deferred.reject(error);
      if (this.err) {
        this.err.textContent = 'Something went wrong. Please try again.';
        this.err.hidden = false;
      }
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('again-free-pair')) customElements.define('again-free-pair', AgainFreePair);

// Again — opt-in shipping protection toggle (default OFF, never pre-ticked).
class AgainProtection extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.input = this.querySelector('input');
    this.input?.addEventListener('change', () => this.toggle(this.input.checked));
  }

  async toggle(on) {
    const wrap = this.closest('.ag-protect');
    wrap?.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const id = String(this.dataset.variantId);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: on ? 'add' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: id, quantity: on ? 1 : 0 }],
        promise: deferred.promise,
      })
    );
    try {
      let res;
      if (on) {
        res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sectionIds.join(','), sections_url: window.location.pathname }),
        });
      } else {
        res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: this.dataset.lineKey || id, quantity: 0, sections: sectionIds.join(','), sections_url: window.location.pathname }),
        });
      }
      const data = await res.json();
      const failed = !res.ok || Boolean(data.status);
      if (failed) this.input.checked = !on;
      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: failed ? undefined : data.sections, source: 'again-protection', didError: failed },
      });
    } catch (error) {
      deferred.reject(error);
      this.input.checked = !on;
    } finally {
      wrap?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('again-protection')) customElements.define('again-protection', AgainProtection);

// Orphaned protection: protection belongs to shoes. If the cart has a protection line but no Again
// shoe (product_type "Shoes", as in snippet again-bundle) — all shoes removed, or only an insole / slides
// left — remove the protection line, so she never pays for it on an accessory-only order or sees an
// "empty" drawer with a $4.95 total.
let againOrphanBusy = false;
async function againRemoveOrphanProtection() {
  const pid = Number(window.AgainProtection?.productId) || 0;
  if (!pid || againOrphanBusy) return;
  const root = window.Shopify?.routes?.root || '/';
  try {
    againOrphanBusy = true;
    const cart = await (await fetch(`${root}cart.js`)).json();
    const prot = cart.items.filter((i) => i.product_id === pid);
    if (!prot.length || cart.items.some((i) => i.product_type === 'Shoes')) return;
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const updates = {};
    prot.forEach((i) => (updates[i.key] = 0));
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: prot.map((i) => ({ merchandiseId: String(i.variant_id), quantity: 0 })),
        promise: deferred.promise,
      })
    );
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sectionIds.join(','), sections_url: window.location.pathname }),
    });
    const data = await res.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { items: data.items || [], sections: data.sections, source: 'again-protection-orphan', didError: !res.ok },
    });
  } catch (e) {
    /* next cart change retries */
  } finally {
    againOrphanBusy = false;
  }
}

if (!window.__againOrphanWatch) {
  window.__againOrphanWatch = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action === 'add') return;
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => setTimeout(againRemoveOrphanProtection, 50));
  });
  againRemoveOrphanProtection();
}

// Cart reservation timer (snippet again-cart-extras, part 'top'). One end time per browser session, kept across
// drawer re-renders and page loads; after it runs out the expired line shows until the next re-render, which
// starts a fresh window.
class AgainReserve extends HTMLElement {
  static KEY = 'ag-reserve-end';

  connectedCallback() {
    const mins = Number(this.dataset.minutes) || 10;
    let end = 0;
    try {
      end = Number(sessionStorage.getItem(AgainReserve.KEY)) || 0;
    } catch (e) {}
    if (end < Date.now()) {
      end = Date.now() + mins * 60000;
      try {
        sessionStorage.setItem(AgainReserve.KEY, String(end));
      } catch (e) {}
    }
    this.end = end;
    this.time = this.querySelector('[data-reserve-time]');
    this.live = this.querySelector('[data-reserve-live]');
    this.over = this.querySelector('[data-reserve-over]');
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, Math.round((this.end - Date.now()) / 1000));
    if (this.time) this.time.textContent = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
    this.classList.toggle('is-low', left > 0 && left <= 120);
    if (left === 0) {
      clearInterval(this.timer);
      if (this.live) this.live.hidden = true;
      if (this.over) this.over.hidden = false;
      this.classList.add('is-over');
    }
  }
}

if (!customElements.get('again-reserve')) customElements.define('again-reserve', AgainReserve);
